// src/path/to/EmergencyPartPurchase.tsx
import React, { useEffect, useRef, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import Button from "../../../components/elements/button";
import { useAppLoader } from "../../../_common/pageLoader/appLoaderHook";
import { useSpearPartsApi } from "../../../store/api/hooks/spearPartsApiHook";
import { useWorkOrderApi } from "../../../store/api/hooks/workOrderApiHook";
import toast from "react-hot-toast";
import moment from "moment";
import { useUserSelector } from "../../../store/selectors/userSelector";
import { useCommonApi } from "../../../store/api/hooks/commonApiHook";
import { useReceiptApi } from "../../../store/api/hooks/receiptApiHook";
import { usePurchaseOrderApi } from "../../../store/api/hooks/purchaseOrderApiHook";
import SelectBox from "../../../components/elements/form/selectBox";
import CreateEmergencyPurchaseOrderParts from "../../Purchase/Order/_components/createEmergencyPurchaseOrderParts";
import FileUpload from "../../../components/elements/form/fileUplaod";
import { useVendorsApi } from "../../../store/api/hooks/vendorsApiHook";

interface AssetsFormProps {
    _ref?: any;
    wid?: any;
    onCreate?: () => void;
    onCancel?: () => void;
}

type AssetsFormValueType = {
    sparePartID?: { value: string; label: string, code: string } | null;
    partname?: { value: string; label: string } | null;
    rate?: any;
    requiredQty?: any;
    paymentMethod?: string;
    vendorID?: { value: string; label: string } | null;
    documentFiles?: any;
};

const EmergencyPartPurchase: React.FC<AssetsFormProps> = ({ _ref, wid, onCreate = () => { }, onCancel = () => { } }) => {
    const { control, handleSubmit, setValue, reset, formState: { errors }, watch } = useForm<AssetsFormValueType>({
        defaultValues: {
            sparePartID: null,
            partname: null,
            rate: null,
            requiredQty: null,
            paymentMethod: 'creditCard',
            vendorID: null,
            documentFiles: null,
        }
    });
    const { showLoader, hideLoader } = useAppLoader();
    const [parts, setParts] = useState<any[]>([]); // optional UI mirror
    const partsRef = useRef<any[]>([]); // holds freshest parts for submit
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [documentAttachments, setDocumentAttachments] = useState<FileList | null>(null);
    const [departmentID, setdepartmentID] = useState<number | null>(null);
    const [vendorList, setVendorList] = useState<any[]>([]);

    const sparePartsAPI = useSpearPartsApi();
    const workOrderAPI: any = useWorkOrderApi();
    const commonApi = useCommonApi();
    const receiptApi = useReceiptApi();
    const purchaseOrderAPI = usePurchaseOrderApi();
    const logedinuser: any = useUserSelector();
    const purchaseApi = usePurchaseOrderApi();
    const vendorApi = useVendorsApi();

    // Keep track of initial parts from GetWorkOrderByID
    const [initialParts, setInitialParts] = useState<any[]>([]);
    const [updatePartWithNewPrice, setUpdatePartWithNewPrice] = useState<boolean>(false);

    // Store WO facility information
    const [workOrderFacility, setWorkOrderFacility] = useState<any>(null);

    // Watch payment method to show/hide vendor dropdown
    const paymentMethod = watch('paymentMethod');

    const changeDocumentAttachmentHandler = (e: any) => {
        setDocumentAttachments(e.target.files);
    };

    // Vendor search function
    const handleVendorSearch = async (inputval: any): Promise<any[]> => {
        return new Promise((resolve, reject) => {
            const payload:any = {serviceTypeIDs: 6, pageSize:0, pageNumber:1};
            if(inputval && inputval.length >= 2){
                payload.searchParam = inputval;
            }
            vendorApi.GetVendorList(payload, (resp: any) => {
                if (resp && resp.hasOwnProperty(`vendorList`) && Array.isArray(resp.vendorList) && resp.vendorList.length > 0) {
                    const vendorOptions = resp.vendorList.map((vendor: any) => {
                        return {
                            label: vendor.vendorName,
                            value: vendor.vendorID,
                        };
                    }).sort((a: any, b: any) => a.label.localeCompare(b.label));
                    setVendorList(vendorOptions);
                    resolve(vendorOptions);
                } else {
                    setVendorList([]);
                    resolve([]);
                }
            }, (error: any) => {
                setVendorList([]);
                reject([])
            })
        });
    };

    // Get vendors for a specific part
    const handleVendorsSearch = async (partID: any): Promise<any[]> => {
        return new Promise((resolve, reject) => {
            sparePartsAPI.getSpearPartsList({ partsID: partID }, (data: any) => {
                const partDetails = data?.sparePartsList ? data.sparePartsList[0] : null;
                resolve(partDetails?.vendors || []);
            }, (error: any) => {
                resolve([]);
            });
        });
    };

    // Map part to vendor with pricing
    const mapPartVendor = async (payload: any) => {
        const vendorresp: any = await new Promise((resolve, reject) => {
            sparePartsAPI.SavePartsVendorMap(payload, (data: any) => {
                resolve(data);
            }, (error: any) => {
                reject(error);
            });
        }).catch((error: any) => {
            console.error("Error saving vendor map:", error);
            return null;
        });
        return vendorresp;
    };

    // Get Purchase Order Code
    const getCode = () => {
        const payload = {};
        purchaseOrderAPI.getOrderCode(payload, (resp: any) => {
            return resp.code;
        }, (error: any) => {
            console.error('Error getting order code:', error);
            return null;
        });
    };

    // receive formatted parts from child
    const handleChildPartsChange = (updatedParts: any[]) => {
        // Merge initial parts from GetWorkOrderByID with new parts from child
        const mergedParts = [
            ...initialParts, // existing parts from Task
            ...(Array.isArray(updatedParts) ? updatedParts : []) // new parts from child
        ];

        // always keep ref in sync to avoid stale data during submit
        partsRef.current = mergedParts;
        setParts(mergedParts); // optional, for local UI if needed
        console.log('[Child -> Parent] merged parts (initial + new):', mergedParts);
        console.log('[Child -> Parent] initial parts count:', initialParts.length);
        console.log('[Child -> Parent] new parts count:', updatedParts?.length || 0);
    };

    const getWorkOrderDetails = () => {
        if (!wid) return;
        const payload = { WorkOrderID: wid };
        workOrderAPI.GetWorkOrderByID(payload, (resp: any) => {
            setdepartmentID(resp.departmentID);
            // Store Task facility information for receipt creation
            if (resp && resp.facilityID && resp.facilityName) {
                setWorkOrderFacility({
                    facilityID: resp.facilityID,
                    facilityName: resp.facilityName
                });
            }

            if (resp && resp.workOrderPartsMappingList) {
                const bind_parts = resp.workOrderPartsMappingList.map((part: any) => {
                    return {
                        woSparePartMappingID: part.woSparePartMappingID,
                        sparePartID: part.partsID,
                        assetID: part.assetID,
                        requiredQty: part.requiredQty,
                        pickedQty: part.pickedQty,
                        usedQty: part.usedQty,
                        facilityID: part.facilityID ? part.facilityID : null,
                        subFacilityID: part.subFacilityID ? part.subFacilityID : null,
                        isPicked: false,
                        isLocked: false,
                        partCode: part.partsCode,
                        partName: part.sparePartsName,
                        rate: part.rate ?? undefined
                    };
                });
                // Store initial parts separately for merging later
                setInitialParts(bind_parts);
                // keep both ref and state in sync with initial parts only
                partsRef.current = bind_parts;
                setParts(bind_parts);
            } else {
                // no parts - reset both initial parts and current parts
                setInitialParts([]);
                partsRef.current = [];
                setParts([]);
            }
        }, (error: any) => {
            toast.error('Failed to fetch Task details.');
            console.error('GetWorkOrderByID error:', error);
        });
    };

    useEffect(() => {
        getWorkOrderDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wid]);

    // Step 1: create a Purchase Order for WO facility with the items selected
    const createEmergencyPurchaseOrder = async (parts: any[], paymentMethod: string, vendorInfo: any) => {
        try {
            if (!workOrderFacility || !workOrderFacility.facilityID) {
                throw new Error('Task facility information not available');
            }

            // show loader while creating PO
            showLoader();

            // Get Purchase Order Code
            let orderCode = `PO-${Date.now()}`;
            try {
                const orderCodeResp: any = await new Promise((resolve, reject) => {
                    purchaseOrderAPI.getOrderCode({}, (resp: any) => resolve(resp), (error: any) => reject(error));
                });
                if (orderCodeResp?.code) orderCode = orderCodeResp.code;
            } catch (err) {
                console.warn('[createEmergencyPurchaseOrder] could not fetch order code, using fallback:', err);
            }

            // Build purchase order payload
            const payload: any = {
                purchaseOrderCode: orderCode,
                departmentID: departmentID,
                facilityID: workOrderFacility.facilityID,
                subFacilityID: null,
                vendorID: vendorInfo ? vendorInfo.value : null,
                taxID: null,
                purchaseOrderTypeID: 1, // Parts type
                purchaseOrderStatusID: 2, // Pending Approval
                requesterID: logedinuser?.userID || 0,
                requiredByDate: moment().format('YYYY-MM-DD'),
                notes: `Emergency Purchase Order for Task ${wid} - Payment Method: ${paymentMethod}${vendorInfo ? ` - Vendor: ${vendorInfo.label}` : ''}`,
                purchaseRequestID: null,
                isEmergency: true,
                purchaseOrderDetails: (parts || []).map((part: any) => {
                    const qty = Number(part.requiredQty || 0);
                    const unitPrice = Number((part.rate ?? part.unitPrice) || 0);
                    return {
                        partsID: part.sparePartID ?? part.partsID,
                        vendorID: vendorInfo ? vendorInfo.value : null,
                        requiredQty: qty,
                        unitID: part.partDetails?.unitID || part.unitID || 5, // Default unit ID
                        unitPrice: unitPrice,
                        totalAmount: Number((qty * unitPrice).toFixed(2))
                    };
                })
            };

            // Validate that we have parts
            if (!parts || parts.length === 0) {
                throw new Error('No parts provided for purchase order');
            }

            console.log('[createEmergencyPurchaseOrder] Purchase Order payload:', payload);

            // Create the purchase order
            const poResp: any = await new Promise((resolve, reject) => {
                purchaseOrderAPI.saveOrder(payload, (resp: any, message: string) => {
                    console.log('[createEmergencyPurchaseOrder] Purchase Order response:', resp, message);
                    if (resp && resp.returnID) {
                        // attach message for convenience
                        resolve({ ...resp, message });
                    } else {
                        reject(resp || new Error('Purchase Order creation failed'));
                    }
                }, (error: any) => {
                    console.error('[createEmergencyPurchaseOrder] Purchase Order error:', error);
                    reject(error);
                });
            });

            console.log('[createEmergencyPurchaseOrder] ✅ Purchase Order created successfully:', poResp);
            toast.success(poResp.message || 'Emergency Purchase Order created successfully');

            // Now, ONLY if PO creation succeeded, call processOrder to approve it
            const returnID = poResp.returnID;
            if (!returnID) {
                throw new Error('Purchase Order created but returnID missing. Cannot process/approve PO.');
            }

            // Build process payload
            const processPayload = {
                purchaseOrderID: returnID,
                PurchaseOrderStatusID: 3 // approve
            };

            console.log('[createEmergencyPurchaseOrder] Calling processOrder with payload:', processPayload);

            const processResp: any = await new Promise((resolve, reject) => {
                // Try purchaseOrderAPI.processOrder first if available, otherwise fallback to purchaseApi.processOrder
                const caller = (typeof purchaseOrderAPI.processOrder === 'function') ? purchaseOrderAPI.processOrder : purchaseApi.processOrder;
                if (typeof caller !== 'function') {
                    reject(new Error('No processOrder function found on purchaseOrderAPI or purchaseApi'));
                    return;
                }

                caller(processPayload, (resp: any) => {
                    console.log('[createEmergencyPurchaseOrder] processOrder response:', resp);
                    // backend may return different success flags - check common patterns
                    if (resp && (resp.successful === true || resp.code === 200 || resp.returnID || resp.status === 'success')) {
                        resolve(resp);
                    } else {
                        // If resp includes message, pass it along
                        reject(resp || new Error('processOrder failed'));
                    }
                }, (error: any) => {
                    console.error('[createEmergencyPurchaseOrder] processOrder error:', error);
                    reject(error);
                });
            });

            console.log('[createEmergencyPurchaseOrder] ✅ Purchase Order processed/approved successfully:', processResp);
            toast.success('Purchase Order approved successfully');

            // hide loader and return both responses
            hideLoader();
            return { poResp, processResp };
        } catch (error: any) {
            console.error('[createEmergencyPurchaseOrder] ❌ Purchase Order creation/processing failed:', error);
            hideLoader();
            // bubble up so caller can decide next steps
            throw error;
        }
    };



    // Step 1: Create Purchase Receipt with optional PO/Vendor reference
    const createPurchaseReceipt = async (parts: any[], paymentMethod: string, vendorInfo: any, purchaseOrderDetails?: any) => {
        try {
            console.log('[Step 1] Creating purchase receipt...');

            if (!workOrderFacility || !workOrderFacility.facilityID) {
                throw new Error('Task facility information not available');
            }

            // Get receipt code
            const receiptCodeResp: any = await new Promise((resolve, reject) => {
                receiptApi.GetReciptCode({}, (resp: any) => {
                    resolve(resp);
                }, (error: any) => {
                    reject(error);
                });
            });

            const receiptCode = receiptCodeResp?.code || `REC-${Date.now()}`;

            // Build receipt payload - with optional PO/Vendor reference, using WO facility
            const receiptPayload = {
                receiptCode: receiptCode,
                facilityID: workOrderFacility.facilityID,
                facilityName: workOrderFacility.facilityName,
                purchaseOrderID: purchaseOrderDetails ? purchaseOrderDetails.purchaseOrderID : null,
                purchaseOrderCode: purchaseOrderDetails ? purchaseOrderDetails.purchaseOrderCode : null,
                refWorkOrderID: wid,
                notes: `Emergency Purchase for Task ${wid}${purchaseOrderDetails ? ` - PO: ${purchaseOrderDetails.purchaseOrderCode}` : ''} - Payment Method: ${paymentMethod}${vendorInfo ? ` - Vendor: ${vendorInfo.label}` : ''}`,
                receiptDate: moment().utc().toISOString(),
                vendorID: purchaseOrderDetails ? purchaseOrderDetails.vendorID : (vendorInfo ? vendorInfo.value : null),
                billComVendorID: purchaseOrderDetails ? purchaseOrderDetails.billComVendorID : (vendorInfo ? vendorInfo.billComVendorID : null),
                billComDepartmentID: purchaseOrderDetails ? purchaseOrderDetails.billComDepartmentID : (vendorInfo ? vendorInfo.billComDepartmentID : null),
                receiptPartsDetails: parts.map((part: any) => ({
                    partsID: part.sparePartID ?? part.partsID,
                    partsName: part.partName ?? part.vendorPartName,
                    partsCode: part.partCode ?? part.vendorPartCode,
                    vendorPartCode: part.partCode ?? part.vendorPartCode ?? '',
                    orderedQuantity: Number(part.requiredQty || 0),
                    unitCost: Number(part.rate ?? part.unitPrice) || 0,
                    unitID: 5, // Default unit ID
                    requiredQuantity: Number(part.requiredQty || 0),
                    totalCost: (Number(part.requiredQty || 0) * Number((part.rate ?? part.unitPrice) || 0))
                })),
                receiptAssetDetails: []
            };

            console.log('[Step 1] Receipt payload:', receiptPayload);

            const receiptResp: any = await new Promise((resolve, reject) => {
                receiptApi.SavePurchaseRecipt(receiptPayload, (resp: any) => {
                    console.log('[Step 1] Receipt response:', resp);
                    if (resp && resp.code === 200 && resp.successful === true) {
                        resolve(resp);
                    } else {
                        reject(resp || new Error('Receipt creation failed'));
                    }
                }, (error: any) => {
                    console.error('[Step 1] Receipt error:', error);
                    reject(error);
                });
            });

            console.log('[Step 1] Purchase receipt created successfully:', receiptResp);
            return receiptResp;
        } catch (error: any) {
            console.error('[Step 1] Purchase receipt creation failed:', error);
            throw error;
        }
    };

    // Step 2: Stock pickup from WO facility to current user automatically  
    const createStockPickup = async (parts: any[], paymentMethod: string, vendorInfo: any) => {
        try {
            console.log('[Step 2] Creating stock pickup...');

            if (!workOrderFacility || !workOrderFacility.facilityID) {
                throw new Error('Task facility information not available');
            }

            // Build stock pickup payload - matching createStockpicking.tsx structure
            const payload = [
                {
                    facilityID: workOrderFacility.facilityID,
                    //refWorkorderID: wid,
                    pickNo: Date.now(), // or generate a unique pick number if needed
                    pickingDate: new Date().toISOString(),
                    pickedByID: logedinuser.userID,
                    pickNote: `Emergency Purchase Stock Pickup - From ${workOrderFacility.facilityName} to ${logedinuser.lastName} ${logedinuser.firstName} on ${moment().format('MM/DD/YYYY hh:mm A')} - Payment Method: ${paymentMethod}${vendorInfo ? ` - Vendor: ${vendorInfo.label}` : ''}`,
                    stockPickDetails: parts.map((part: any) => {
                        const qty = Number(part.requiredQty) || 0;
                        const rate = Number(part.rate ?? part.avgUnitCost) || 0;
                        return {
                            partsID: part.sparePartID ?? part.partsID,
                            pickQuantity: qty,
                            partsCostRate: rate,
                            partsTotalAmount: rate * qty,
                            unitID: part.partDetails?.unitID || part.unitID || 0,
                        };
                    })

                }
            ];

            const pickupPayload: any = {};
            pickupPayload.request = payload;

            console.log('[Step 2] Stock pickup payload:', pickupPayload);

            const pickupResp: any = await new Promise((resolve, reject) => {
                workOrderAPI.InsertStockPicking(pickupPayload, (resp: any) => {
                    console.log('[Step 2] Stock pickup response:', resp);
                    if (resp && resp.code === 200 && resp.successful === true) {
                        resolve(resp);
                    } else {
                        reject(resp || new Error('Stock pickup failed'));
                    }
                }, (error: any) => {
                    console.error('[Step 2] Stock pickup error:', error);
                    reject(error);
                });
            });

            console.log('[Step 2] Stock pickup created successfully:', pickupResp);
            return pickupResp;
        } catch (error: any) {
            console.error('[Step 2] Stock pickup creation failed:', error);
            throw error;
        }
    };

    const onSubmit: SubmitHandler<AssetsFormValueType> = async (data) => {
        // Handle different payment methods
        if (data.paymentMethod === 'creditCard') {
            console.log('Processing Credit Card Payment...');
            // Credit Card payment logic

            setIsSubmitting(true);
            try {
                const currentParts = partsRef.current || [];
                if (!currentParts || currentParts.length === 0) {
                    toast.error('Please add at least one part.');
                    setIsSubmitting(false);
                    return;
                }

                const validParts = currentParts.filter((p: any) => Number(p.requiredQty) > 0 || p.woSparePartMappingID);
                if (!validParts || validParts.length === 0) {
                    toast.error('Please enter quantity for at least one part.');
                    setIsSubmitting(false);
                    return;
                }

                // Get new parts (exclude initial parts for receipt/pickup)
                const newPartsForReceiptAndPickup = validParts.filter((part: any) => !part.woSparePartMappingID);
                showLoader();



                // Step 1: Create Purchase Receipt (only for new parts, without PO/Vendor reference)
                let receiptResponse = null;
                if (newPartsForReceiptAndPickup.length > 0) {
                    try {
                        receiptResponse = await createPurchaseReceipt(newPartsForReceiptAndPickup, data.paymentMethod!, data.vendorID);
                        console.log('[Step 1] ✅ Purchase receipt created successfully');
                        
                        // Upload documents if available and receipt was created successfully
                        if (documentAttachments && documentAttachments.length > 0 && receiptResponse && receiptResponse.returnID) {
                            try {
                                const formData = new FormData();
                                formData.append('ReceiptID', receiptResponse.returnID);
                                let i = 0;
                                for (let j = 0; j < documentAttachments.length; j++) {
                                    const file = documentAttachments[j];
                                    formData.append('InputFile' + i, file);
                                    i++;
                                }
                                const uploadResp: any = await new Promise((resolve, reject) => {
                                    receiptApi.UploadAttachment(formData, (resp: any) => {
                                        resolve(resp);
                                    }, (error: any) => {
                                        reject(error);
                                    });
                                });
                                if (uploadResp && uploadResp.code === 200 && uploadResp.successful === true) {
                                    toast.success('Documents uploaded successfully');
                                } else {
                                    toast.error('Failed to upload documents');
                                }
                            } catch (uploadError: any) {
                                console.error('Document upload error:', uploadError);
                                toast.error('Failed to upload documents');
                            }
                        }
                    } catch (error: any) {
                        console.error('[Step 1] ❌ Purchase receipt creation failed:', error);
                        throw new Error(`Step 1 Failed - Purchase Receipt: ${error?.message || error}`);
                    }

                    // Step 2: Stock Pickup (from WO facility to current user automatically)
                    try {
                        await createStockPickup(newPartsForReceiptAndPickup, data.paymentMethod!, data.vendorID);
                        console.log('[Step 2] ✅ Stock pickup created successfully');
                    } catch (error: any) {
                        console.error('[Step 2] ❌ Stock pickup creation failed:', error);
                        throw new Error(`Step 2 Failed - Stock Pickup: ${error?.message || error}`);
                    }
                } else {
                    console.log('[Steps 1-2] Skipped - No new parts to create receipt and pickup for');
                }

                // Step 3: Add items to Task (existing functionality)
                try {
                    console.log('[Step 3] Adding items to Task...');

                    // Build payload for SaveWorkOrderInventory
                    const payload: any = {
                        workOrderID: wid,
                        woSpareParts: validParts.map((p: any) => ({
                            woSparePartMappingID: p.woSparePartMappingID ?? null,
                            sparePartID: p.sparePartID ?? p.partsID ?? null,
                            assetID: p.assetID ?? null,
                            requiredQty: (typeof p.requiredQty === 'string') ? p.requiredQty : Number(p.requiredQty || 0),
                            pickedQty: p.pickedQty ?? 0,
                            usedQty: p.usedQty ?? 0,
                            facilityID: p.facilityID ?? null,
                            isPicked: p.isPicked ?? false,
                            isLocked: p.isLocked ?? false,
                            partCode: p.partCode ?? p.vendorPartCode ?? '',
                            partName: p.partName ?? p.vendorPartName ?? '',
                            rate: p.rate ?? p.unitPrice ?? undefined,
                            subFacilityID: p.subFacilityID ?? p.subFaclityID ?? null,
                        }))
                    };

                    console.log('Final SaveWorkOrderInventory payload:', payload);

                    // Call SaveWorkOrderInventory
                    await new Promise((resolve, reject) => {
                        workOrderAPI.SaveWorkOrderInventory(payload, (resp: any) => {
                            console.log('SaveWorkOrderInventory response:', resp);
                            if (resp && resp.code === 200 && resp.successful === true) {
                                resolve(resp);
                            } else {
                                reject(resp || new Error('SaveWorkOrderInventory failed'));
                            }
                        }, (err: any) => {
                            console.error('SaveWorkOrderInventory network/error callback:', err);
                            reject(err);
                        });
                    });

                    // Build and call InsertStockPicking for WO linkage
                    const payloadPick: any = {
                        request: [
                            {
                                pickNo: Date.now(),
                                pickingDate: moment(moment().format(`YYYY-MM-DD HH:mm:ss`), `YYYY-MM-DD HH:mm:ss`).utc().toISOString(),
                                refWorkorderID: wid,
                                pickedByID: logedinuser.userID,
                                facilityID: null,
                                pickNote: `Emergency Purchase - Picked by ${typeof logedinuser.userID === 'object' ? logedinuser.userID.label : logedinuser.userID} on ${moment().format('MM/DD/YYYY hh:mm A')} - Payment Method: ${data.paymentMethod}`,
                                subFacilityID: null,
                                stockPickDetails: [],
                                paymentMethod: data.paymentMethod,

                            }
                        ]
                    };

                    payloadPick.request[0].stockPickDetails = validParts.map((item: any) => {
                        const qty = Number(item.requiredQty) || 0;
                        const rate = Number(item.rate ?? item.unitPrice) || 0;
                        return {
                            partsID: item.sparePartID ?? item.partsID ?? null,
                            pickQuantity: qty,
                            partsCostRate: rate,
                            partsTotalAmount: rate * qty,
                            unitID: item.partDetails?.unitID || item.unitID || 0,
                            pickedByID: logedinuser.userID,
                            paymentMethod: data.paymentMethod,
                            ...(data.paymentMethod === 'onAccount' && data.vendorID && {
                                vendorID: data.vendorID.value,
                                vendorName: data.vendorID.label
                            })
                        };
                    });

                    /* console.log('InsertStockPicking payload:', payloadPick);
    
                    await new Promise((resolve, reject) => {
                        workOrderAPI.InsertStockPicking(payloadPick, (resp2: any) => {
                            console.log('InsertStockPicking response:', resp2);
                            if (resp2 && resp2.code === 200 && resp2.successful === true) {
                                resolve(resp2);
                            } else {
                                reject(resp2 || new Error('InsertStockPicking failed'));
                            }
                        }, (err2: any) => {
                            console.error('InsertStockPicking error/callback:', err2);
                            reject(err2);
                        });
                    }); */

                    console.log('[Step 3] ✅ Task items added successfully');
                } catch (error: any) {
                    console.error('[Step 3] ❌ Task items addition failed:', error);
                    throw new Error(`Step 3 Failed - Add Items to WO: ${error?.message || error}`);
                }

                // Step 4: Update vendor pricing if checkbox is checked
                if (updatePartWithNewPrice && data.vendorID) {
                    console.log('[Step 4] Updating vendor pricing for parts...');
                    for (const part of validParts) {
                        const partID = part.sparePartID ?? part.partsID;
                        const unitPrice = Number(part.rate ?? part.unitPrice) || 0;
                        
                        try {
                            const vendors = await handleVendorsSearch(partID);
                            console.log("Fetched vendors for part:", partID, vendors);
                            
                            let mappedVendor: any = null;
                            for (const vendor of vendors) {
                                if (+vendor.vendorID === +data.vendorID.value) {
                                    mappedVendor = vendor;
                                    break;
                                }
                            }

                            if (mappedVendor) {
                                // Update existing part vendor pricing
                                console.log('Updating existing part vendor map for part:', partID);
                                const payload = {
                                    partsID: partID,
                                    vendorID: data.vendorID.value,
                                    isPreferred: mappedVendor.isPreferred,
                                    vendorPartCode: mappedVendor.vendorPartCode || '',
                                    costPerUnit: unitPrice,
                                    partsVendorMapID: mappedVendor.partsVendorMapID
                                };
                                await mapPartVendor(payload);
                            } else {
                                // Create new part vendor pricing
                                console.log('Creating new part vendor map for part:', partID);
                                const payload = {
                                    partsID: partID,
                                    vendorID: data.vendorID.value,
                                    isPreferred: false,
                                    vendorPartCode: '',
                                    costPerUnit: unitPrice
                                };
                                await mapPartVendor(payload);
                            }
                        } catch (vendorError) {
                            console.error('Error updating vendor pricing for part:', partID, vendorError);
                        }
                    }
                    console.log('[Step 4] ✅ Vendor pricing updated successfully');
                }

                // All steps completed successfully
                toast.success('Emergency part purchase completed successfully! Purchase receipt created, stock picked up, and items added to Task.');
                onCreate();
                formReset();
                // Reset both parts arrays after successful save
                setInitialParts([]);
                setParts([]);
                partsRef.current = [];
            } catch (err: any) {
                console.error('Sequential workflow error:', err);
                toast.error(err?.message || 'Failed to complete emergency part purchase. Check console/network.');
            } finally {
                hideLoader();
                setIsSubmitting(false);
            }

        } else if (data.paymentMethod === 'onAccount') {

            setIsSubmitting(true);
            try {
                // Validate vendor selection for "On Account" payment
                if (data.paymentMethod === 'onAccount' && !data.vendorID) {
                    toast.error('Please select a vendor for on account payment.');
                    setIsSubmitting(false);
                    return;
                }

                const currentParts = partsRef.current || [];
                console.log('onSubmit form data:', data);
                console.log('onSubmit parts (from ref - merged initial + new):', currentParts);
                console.log('onSubmit initial parts count:', initialParts.length);
                console.log('onSubmit total parts count:', currentParts.length);

                if (!currentParts || currentParts.length === 0) {
                    toast.error('Please add at least one part.');
                    setIsSubmitting(false);
                    return;
                }

                const validParts = currentParts.filter((p: any) => Number(p.requiredQty) > 0 || p.woSparePartMappingID);
                if (!validParts || validParts.length === 0) {
                    toast.error('Please enter quantity for at least one part.');
                    setIsSubmitting(false);
                    return;
                }

                // Get new parts (exclude initial parts for receipt/pickup)
                const newPartsForReceiptAndPickup = validParts.filter((part: any) => !part.woSparePartMappingID);

                console.log('New parts for receipt and pickup:', newPartsForReceiptAndPickup);
                console.log('All valid parts for WO addition:', validParts);

                showLoader();

                // Step 0: Create Emergency Purchase Order and get details for new parts
                let purchaseOrderDetails = null;
                if (newPartsForReceiptAndPickup.length > 0) {
                    try {
                        const emergencyPoResponse = await createEmergencyPurchaseOrder(newPartsForReceiptAndPickup, data.paymentMethod!, data.vendorID);
                        // Get the created purchase order details using the returned PO ID
                        const createdPoId = emergencyPoResponse?.poResp?.returnID;
                        if (!createdPoId) {
                            throw new Error('Purchase Order created but no returnID received');
                        }

                        const poResp: any = await new Promise((resolve, reject) => {
                            purchaseOrderAPI.getOrderById({ purchaseOrderID: createdPoId }, (resp: any) => {
                                if (resp && resp.purchaseOrderID) {
                                    resolve(resp);
                                } else {
                                    reject(resp || new Error('Failed to get purchase order details'));
                                }
                            }, (error: any) => {
                                console.error('[Step 0] Purchase Order details error:', error);
                                reject(error);
                            });
                        });

                        purchaseOrderDetails = poResp;
                    } catch (error: any) {
                        throw new Error(`Step 0 Failed - Emergency Purchase Order: ${error?.message || error}`);
                    }
                }

                // Step 1: Create Purchase Receipt (only for new parts, with PO/Vendor reference if available)
                let receiptResponse = null;
                if (newPartsForReceiptAndPickup.length > 0) {
                    try {
                        receiptResponse = await createPurchaseReceipt(newPartsForReceiptAndPickup, data.paymentMethod!, data.vendorID, purchaseOrderDetails);
                        // Upload documents if available and receipt was created successfully
                        if (documentAttachments && documentAttachments.length > 0 && receiptResponse && receiptResponse.returnID) {
                            try {
                                const formData = new FormData();
                                formData.append('ReceiptID', receiptResponse.returnID);
                                let i = 0;
                                for (let j = 0; j < documentAttachments.length; j++) {
                                    const file = documentAttachments[j];
                                    formData.append('InputFile' + i, file);
                                    i++;
                                }
                                const uploadResp: any = await new Promise((resolve, reject) => {
                                    receiptApi.UploadAttachment(formData, (resp: any) => {
                                        resolve(resp);
                                    }, (error: any) => {
                                        reject(error);
                                    });
                                });
                                if (uploadResp && uploadResp.code === 200 && uploadResp.successful === true) {
                                    toast.success('Documents uploaded successfully');
                                } else {
                                    toast.error('Failed to upload documents');
                                }
                            } catch (uploadError: any) {
                                console.error('Document upload error:', uploadError);
                                toast.error('Failed to upload documents');
                            }
                        }
                    } catch (error: any) {
                        console.error('[Step 1] ❌ Purchase receipt creation failed:', error);
                        throw new Error(`Step 1 Failed - Purchase Receipt: ${error?.message || error}`);
                    }

                    // Step 2: Stock Pickup (from WO facility to current user automatically)
                    try {
                        await createStockPickup(newPartsForReceiptAndPickup, data.paymentMethod!, data.vendorID);
                        console.log('[Step 2] ✅ Stock pickup created successfully');
                    } catch (error: any) {
                        console.error('[Step 2] ❌ Stock pickup creation failed:', error);
                        throw new Error(`Step 2 Failed - Stock Pickup: ${error?.message || error}`);
                    }
                } else {
                    console.log('[Steps 1-2] Skipped - No new parts to create receipt and pickup for');
                }

                // Step 3: Add items to Task (existing functionality)
                try {
                    console.log('[Step 3] Adding items to Task...');

                    // Build payload for SaveWorkOrderInventory
                    const payload: any = {
                        workOrderID: wid,
                        woSpareParts: validParts.map((p: any) => ({
                            woSparePartMappingID: p.woSparePartMappingID ?? null,
                            sparePartID: p.sparePartID ?? p.partsID ?? null,
                            assetID: p.assetID ?? null,
                            requiredQty: (typeof p.requiredQty === 'string') ? p.requiredQty : Number(p.requiredQty || 0),
                            pickedQty: p.pickedQty ?? 0,
                            usedQty: p.usedQty ?? 0,
                            facilityID: p.facilityID ?? null,
                            isPicked: p.isPicked ?? false,
                            isLocked: p.isLocked ?? false,
                            partCode: p.partCode ?? p.vendorPartCode ?? '',
                            partName: p.partName ?? p.vendorPartName ?? '',
                            rate: p.rate ?? p.unitPrice ?? undefined,
                            subFacilityID: p.subFacilityID ?? p.subFaclityID ?? null,
                        }))
                    };

                    console.log('Final SaveWorkOrderInventory payload:', payload);

                    // Call SaveWorkOrderInventory
                    await new Promise((resolve, reject) => {
                        workOrderAPI.SaveWorkOrderInventory(payload, (resp: any) => {
                            console.log('SaveWorkOrderInventory response:', resp);
                            if (resp && resp.code === 200 && resp.successful === true) {
                                resolve(resp);
                            } else {
                                reject(resp || new Error('SaveWorkOrderInventory failed'));
                            }
                        }, (err: any) => {
                            console.error('SaveWorkOrderInventory network/error callback:', err);
                            reject(err);
                        });
                    });

                    // Build and call InsertStockPicking for WO linkage
                    const payloadPick: any = {
                        request: [
                            {
                                pickNo: Date.now(),
                                pickingDate: moment(moment().format(`YYYY-MM-DD HH:mm:ss`), `YYYY-MM-DD HH:mm:ss`).utc().toISOString(),
                                refWorkorderID: wid,
                                pickedByID: logedinuser.userID,
                                facilityID: null,
                                pickNote: `Emergency Purchase - Picked by ${typeof logedinuser.userID === 'object' ? logedinuser.userID.label : logedinuser.userID} on ${moment().format('MM/DD/YYYY hh:mm A')} - Payment Method: ${data.paymentMethod}${data.paymentMethod === 'onAccount' && data.vendorID ? ` - Vendor: ${data.vendorID.label}` : ''}`,
                                subFacilityID: null,
                                stockPickDetails: [],
                                paymentMethod: data.paymentMethod,
                                ...(data.paymentMethod === 'onAccount' && data.vendorID && {
                                    vendorID: data.vendorID.value,
                                    vendorName: data.vendorID.label
                                })
                            }
                        ]
                    };

                    payloadPick.request[0].stockPickDetails = validParts.map((item: any) => {
                        const qty = Number(item.requiredQty) || 0;
                        const rate = Number(item.rate ?? item.unitPrice) || 0;
                        return {
                            partsID: item.sparePartID ?? item.partsID ?? null,
                            pickQuantity: qty,
                            partsCostRate: rate,
                            partsTotalAmount: rate * qty,
                            unitID: item.partDetails?.unitID || item.unitID || 0,
                            pickedByID: logedinuser.userID,
                            paymentMethod: data.paymentMethod,
                            ...(data.paymentMethod === 'onAccount' && data.vendorID && {
                                vendorID: data.vendorID.value,
                                vendorName: data.vendorID.label
                            })
                        };
                    });
                    console.log('[Step 3] ✅ Task items added successfully');
                } catch (error: any) {
                    console.error('[Step 3] ❌ Task items addition failed:', error);
                    throw new Error(`Step 3 Failed - Add Items to WO: ${error?.message || error}`);
                }

                // Step 4: Update vendor pricing if checkbox is checked
                if (updatePartWithNewPrice && data.vendorID) {
                    console.log('[Step 4] Updating vendor pricing for parts...');
                    for (const part of validParts) {
                        const partID = part.sparePartID ?? part.partsID;
                        const unitPrice = Number(part.rate ?? part.unitPrice) || 0;
                        
                        try {
                            const vendors = await handleVendorsSearch(partID);
                            console.log("Fetched vendors for part:", partID, vendors);
                            
                            let mappedVendor: any = null;
                            for (const vendor of vendors) {
                                if (+vendor.vendorID === +data.vendorID.value) {
                                    mappedVendor = vendor;
                                    break;
                                }
                            }

                            if (mappedVendor) {
                                // Update existing part vendor pricing
                                console.log('Updating existing part vendor map for part:', partID);
                                const payload = {
                                    partsID: partID,
                                    vendorID: data.vendorID.value,
                                    isPreferred: mappedVendor.isPreferred,
                                    vendorPartCode: mappedVendor.vendorPartCode || '',
                                    costPerUnit: unitPrice,
                                    partsVendorMapID: mappedVendor.partsVendorMapID
                                };
                                await mapPartVendor(payload);
                            } else {
                                // Create new part vendor pricing
                                console.log('Creating new part vendor map for part:', partID);
                                const payload = {
                                    partsID: partID,
                                    vendorID: data.vendorID.value,
                                    isPreferred: false,
                                    vendorPartCode: '',
                                    costPerUnit: unitPrice
                                };
                                await mapPartVendor(payload);
                            }
                        } catch (vendorError) {
                            console.error('Error updating vendor pricing for part:', partID, vendorError);
                        }
                    }
                    console.log('[Step 4] ✅ Vendor pricing updated successfully');
                }

                // All steps completed successfully
                toast.success('Emergency part purchase completed successfully! Purchase receipt created, stock picked up, and items added to Task.');
                onCreate();
                formReset();
                // Reset both parts arrays after successful save
                setInitialParts([]);
                setParts([]);
                partsRef.current = [];
            } catch (err: any) {
                console.error('Sequential workflow error:', err);
                toast.error(err?.message || 'Failed to complete emergency part purchase. Check console/network.');
            } finally {
                hideLoader();
                setIsSubmitting(false);
            }
        }
    };

    const formReset = () => {
        reset({
            sparePartID: null,
            partname: null,
            rate: null,
            requiredQty: null,
            paymentMethod: 'creditCard',
            vendorID: null,
            documentFiles: null,
        });
        setDocumentAttachments(null);
    };

    useEffect(() => {
        handleVendorSearch('');
    },[]);

    useEffect(() => {
        if (_ref?.current) {
            _ref.current.reset = formReset;
        }
    }, [_ref]);

    return (
        // IMPORTANT: noValidate disables browser built-in validation that caused "not focusable" error
        <form onSubmit={handleSubmit(onSubmit)} ref={_ref} className="flex flex-col h-full" noValidate>
            <div className="flex-1 overflow-y-auto">
                <div className="flex items-start gap-6 flex-col">
                    <div className="mb-6 w-full">
                        <div className="flex items-center gap-6 pt-4">
                            <p className="font-normal text-[0.938rem] leading-none">Payment Method:</p>
                            <Controller
                                name="paymentMethod"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <div className="flex gap-6 items-center">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                value="creditCard"
                                                checked={value === 'creditCard'}
                                                onChange={() => onChange('creditCard')}
                                                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                            />
                                            <span className="text-sm font-medium text-gray-700">Credit Card</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                value="onAccount"
                                                checked={value === 'onAccount'}
                                                onChange={() => onChange('onAccount')}
                                                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                            />
                                            <span className="text-sm font-medium text-gray-700">On Account</span>
                                        </label>
                                    </div>
                                )}
                            />
                        </div>

                        {/* Vendor Selection - Show only when "On Account" is selected */}

                        <div className="mb-6 w-full">
                            <Controller
                                name="vendorID"
                                control={control}
                                rules={{ required: paymentMethod === 'onAccount' ? 'Vendor is required when paying on account' : false }}
                                render={({ field: { value, onChange } }) => (
                                    <SelectBox
                                        label="Select Vendor"
                                        required={paymentMethod === 'onAccount'}
                                        //async={true}
                                        placeholder="Search and select vendor..."
                                        value={value}
                                        onChange={onChange}
                                        //asyncLoadOptions={handleVendorSearch}
                                        options={vendorList}
                                        isClearable={true}
                                        error={errors.vendorID?.message}
                                    />
                                )}
                            />
                        </div>

                        {watch('vendorID') && (
                            <div className='my-4 flex'>
                                <label className='flex items-center gap-3'>
                                    <input type="checkbox" checked={updatePartWithNewPrice} onChange={(e) => setUpdatePartWithNewPrice(e.target.checked)} />
                                    <span>Update vendor pricing for new price?</span>
                                </label>
                            </div>
                        )}

                        <CreateEmergencyPurchaseOrderParts onPartsChange={handleChildPartsChange} initialParts={[]} />
                        {/* Document Upload for On Account Payment */}

                        <div className="mt-6 w-full">
                            <Controller
                                name="documentFiles"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <FileUpload
                                        label="Upload Supporting Documents"
                                        value={value}
                                        isMulti={true}
                                        accept=".jpg, .jpeg, .png, application/pdf, .pdf, .doc, .docx"
                                        isFigmaType={true}
                                        allowedExt={['png', 'jpg', 'jpeg', 'gif', 'pdf', 'doc', 'docx']}
                                        retainFiles={true}
                                        maxSize={10}
                                        onChange={(e: any, files: any) => {
                                            changeDocumentAttachmentHandler(e);
                                            onChange(e.target.files[0]);
                                        }}
                                        placeholder="Choose documents to upload"
                                        className="!my-4"
                                    />
                                )}
                            />
                        </div>
                        {/* pass handler to child */}

                    </div>
                </div>
            </div>

            <div className="flex justify-end gap-2 flex-shrink-0 pt-4 mt-4 border-t border-gray-200 bg-white">
                <Button type="button" theme="secondary" onClick={() => {
                    formReset();
                    onCancel();
                }}>
                    Cancel
                </Button>
                <Button type="submit" working={isSubmitting}>
                    Save
                </Button>
            </div>
        </form>
    );
};
export default EmergencyPartPurchase;